import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';

const AvailabilityForecast = () => {
    const [loading, setLoading] = useState(false);
    const [forecast, setForecast] = useState(null);
    const [dateRange, setDateRange] = useState({
        start: new Date().toISOString().split('T')[0],
        end: new Date(Date.now() + 180 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    });
    const [error, setError] = useState('');

    // Quick select date ranges
    const setQuickRange = (days) => {
        const start = new Date();
        const end = new Date();
        end.setDate(end.getDate() + days);

        setDateRange({
            start: start.toISOString().split('T')[0],
            end: end.toISOString().split('T')[0]
        });
    };

    const fetchForecast = async () => {
        setLoading(true);
        setError('');

        try {
            const data = await apiGet(
                `/admin/front-office/availability/forecast?startDate=${dateRange.start}&endDate=${dateRange.end}`
            );

            if (data.success) {
                setForecast(data.data);
            } else {
                setError(data.message || 'Failed to fetch forecast');
            }
        } catch (err) {
            console.error('Error fetching forecast:', err);
            setError('Failed to fetch forecast data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchForecast();
    }, []);

    // Generate array of dates between start and end
    const getDatesInRange = () => {
        if (!forecast) return [];

        const dates = [];
        const current = new Date(forecast.startDate);
        const end = new Date(forecast.endDate);

        while (current <= end) {
            dates.push(new Date(current).toISOString().split('T')[0]);
            current.setDate(current.getDate() + 1);
        }

        return dates;
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'available':
                return '#10b981'; // Green
            case 'occupied':
                return '#ef4444'; // Red
            case 'maintenance':
                return '#f59e0b'; // Orange
            case 'cleaning':
                return '#8b5cf6'; // Purple
            default:
                return '#9ca3af'; // Gray
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'available':
                return '✓';
            case 'occupied':
                return '✗';
            case 'maintenance':
                return '🔧';
            case 'cleaning':
                return '🧹';
            default:
                return '-';
        }
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return `${date.getDate()}/${date.getMonth() + 1}`;
    };

    const dates = getDatesInRange();

    return (
        <AdminLayout>
            <div style={styles.container}>
                <div style={styles.header}>
                    <h1 style={styles.title}>📅 Availability Forecast</h1>
                    <p style={styles.subtitle}>View room availability up to 6 months in advance</p>
                </div>

                {/* Info Card */}
                <div style={styles.controlsCard}>
                    <div style={{ textAlign: 'center', padding: '12px' }}>
                        <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '700', color: '#374151' }}>
                            📅 6-Month Availability Forecast
                        </h3>
                        <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>
                            Showing availability from {new Date(dateRange.start).toLocaleDateString()} to {new Date(dateRange.end).toLocaleDateString()}
                        </p>
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div style={styles.errorCard}>
                        ⚠️ {error}
                    </div>
                )}

                {/* Legend */}
                {forecast && (
                    <div style={styles.legendCard}>
                        <span style={styles.legendTitle}>Legend:</span>
                        <div style={styles.legendItem}>
                            <div style={{ ...styles.legendBox, background: '#10b981' }}></div>
                            <span>Available</span>
                        </div>
                        <div style={styles.legendItem}>
                            <div style={{ ...styles.legendBox, background: '#ef4444' }}></div>
                            <span>Occupied</span>
                        </div>
                        <div style={styles.legendItem}>
                            <div style={{ ...styles.legendBox, background: '#f59e0b' }}></div>
                            <span>Maintenance</span>
                        </div>
                        <div style={styles.legendItem}>
                            <div style={{ ...styles.legendBox, background: '#8b5cf6' }}></div>
                            <span>Cleaning</span>
                        </div>
                    </div>
                )}

                {/* Forecast Grid */}
                {loading ? (
                    <div style={styles.loadingCard}>
                        <div style={styles.spinner}></div>
                        <p>Loading forecast data...</p>
                    </div>
                ) : forecast && forecast.rooms ? (
                    <div style={styles.forecastCard}>
                        <div style={styles.gridContainer}>
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={styles.stickyHeader}>Room</th>
                                        <th style={styles.stickyHeader}>Type</th>
                                        {dates.map((date, idx) => (
                                            <th key={idx} style={styles.dateHeader}>
                                                {formatDate(date)}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {forecast.rooms.map((room) => (
                                        <tr key={room.roomId}>
                                            <td style={styles.roomCell}>{room.roomNumber}</td>
                                            <td style={styles.typeCell}>{room.roomType}</td>
                                            {room.dailyStatus.map((day, idx) => (
                                                <td
                                                    key={idx}
                                                    style={{
                                                        ...styles.statusCell,
                                                        background: getStatusColor(day.status)
                                                    }}
                                                    title={
                                                        day.details?.guestName
                                                            ? `Occupied by ${day.details.guestName}`
                                                            : day.status.charAt(0).toUpperCase() + day.status.slice(1)
                                                    }
                                                >
                                                    {getStatusLabel(day.status)}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    !loading && (
                        <div style={styles.emptyCard}>
                            <p>📊 Select a date range and click "Generate Forecast" to view availability</p>
                        </div>
                    )
                )}
            </div>
        </AdminLayout>
    );
};

const styles = {
    container: {
        padding: '20px',
        maxWidth: '100%',
        background: '#f5f7fa'
    },
    header: {
        marginBottom: '24px'
    },
    title: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#1a1a1a',
        margin: '0 0 8px 0'
    },
    subtitle: {
        fontSize: '16px',
        color: '#6b7280',
        margin: 0
    },
    controlsCard: {
        background: 'white',
        padding: '24px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        marginBottom: '20px'
    },
    datePickerSection: {
        display: 'flex',
        gap: '16px',
        alignItems: 'end',
        flexWrap: 'wrap',
        marginBottom: '16px'
    },
    dateInputGroup: {
        flex: '1',
        minWidth: '200px'
    },
    label: {
        display: 'block',
        fontSize: '14px',
        fontWeight: '600',
        color: '#374151',
        marginBottom: '8px'
    },
    dateInput: {
        width: '100%',
        padding: '10px 12px',
        border: '2px solid #e5e7eb',
        borderRadius: '8px',
        fontSize: '14px',
        fontFamily: 'inherit'
    },
    generateButton: {
        padding: '10px 24px',
        background: '#2563eb',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.3s',
        height: '42px'
    },
    quickSelectSection: {
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
        flexWrap: 'wrap',
        paddingTop: '16px',
        borderTop: '1px solid #e5e7eb'
    },
    quickSelectLabel: {
        fontSize: '14px',
        fontWeight: '600',
        color: '#6b7280'
    },
    quickButton: {
        padding: '8px 16px',
        background: '#f3f4f6',
        color: '#374151',
        border: '1px solid #d1d5db',
        borderRadius: '6px',
        fontSize: '13px',
        fontWeight: '500',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    errorCard: {
        background: '#fee2e2',
        color: '#991b1b',
        padding: '16px',
        borderRadius: '8px',
        marginBottom: '20px',
        fontSize: '14px',
        fontWeight: '500'
    },
    legendCard: {
        background: 'white',
        padding: '16px 24px',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        marginBottom: '20px',
        display: 'flex',
        gap: '24px',
        alignItems: 'center',
        flexWrap: 'wrap'
    },
    legendTitle: {
        fontSize: '14px',
        fontWeight: '700',
        color: '#374151'
    },
    legendItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '13px',
        color: '#6b7280'
    },
    legendBox: {
        width: '20px',
        height: '20px',
        borderRadius: '4px'
    },
    forecastCard: {
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        overflow: 'hidden'
    },
    gridContainer: {
        overflowX: 'auto',
        maxHeight: '600px',
        overflowY: 'auto'
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '13px'
    },
    stickyHeader: {
        position: 'sticky',
        top: 0,
        left: 0,
        background: '#f9fafb',
        padding: '12px 16px',
        textAlign: 'left',
        fontWeight: '700',
        color: '#374151',
        borderBottom: '2px solid #e5e7eb',
        zIndex: 10
    },
    dateHeader: {
        background: '#f9fafb',
        padding: '12px 8px',
        textAlign: 'center',
        fontWeight: '600',
        color: '#6b7280',
        borderBottom: '2px solid #e5e7eb',
        minWidth: '50px',
        fontSize: '11px'
    },
    roomCell: {
        padding: '12px 16px',
        fontWeight: '700',
        color: '#1f2937',
        borderBottom: '1px solid #f3f4f6',
        position: 'sticky',
        left: 0,
        background: 'white',
        zIndex: 5
    },
    typeCell: {
        padding: '12px 16px',
        color: '#6b7280',
        borderBottom: '1px solid #f3f4f6',
        fontSize: '12px'
    },
    statusCell: {
        padding: '12px 8px',
        textAlign: 'center',
        borderBottom: '1px solid #f3f4f6',
        color: 'white',
        fontWeight: '700',
        cursor: 'pointer',
        transition: 'opacity 0.2s'
    },
    loadingCard: {
        background: 'white',
        padding: '60px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        textAlign: 'center',
        color: '#6b7280'
    },
    spinner: {
        width: '40px',
        height: '40px',
        border: '4px solid #f3f4f6',
        borderTop: '4px solid #2563eb',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        margin: '0 auto 16px'
    },
    emptyCard: {
        background: 'white',
        padding: '60px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        textAlign: 'center',
        color: '#9ca3af',
        fontSize: '16px'
    }
};

export default AvailabilityForecast;
